import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Tabs,
  Tab,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useUserMeControllerUserMeQuery } from '@shared/services/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import {
  IconArrowLeft,
  IconArrowRight,
  IconClock,
  IconEye,
  IconHistory,
  IconPlus,
  IconSearch,
  IconShield,
  IconStack,
} from '@tabler/icons-react';
import {
  MissionBudgetFields,
  validMissionLimits,
  runtimeBudgetReasonMessage,
} from './MissionBudgetFields';
import { MissionBriefEditor } from './MissionBriefEditor';
import { MissionPlanner } from './MissionPlanner';
import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { isAssistantShortcut } from '@shared/designVariants/assistantShortcut';
import { MissionVersionHistory } from './MissionVersionHistory';
import { MissionSavedChecks } from './MissionSavedChecks';
import { MissionEventFilters } from './MissionEventFilters';
import { MissionConfigurationChecks } from './MissionConfigurationChecks';
import { CampaignPicker } from './CampaignPicker';
import { OperationJournal } from './OperationJournal';
import {
  useArchiveWorkspaceMissionMutation,
  useCreateWorkspaceMissionMutation,
  usePauseWorkspaceMissionMutation,
  usePublishWorkspaceMissionMutation,
  useRunWorkspaceMissionMutation,
  useSaveWorkspaceMissionMutation,
  useWorkspaceMissionQuery,
  useWorkspaceMissionsQuery,
  type Mission,
  type MissionSpec,
  type WorkspaceAction,
} from './workspaceApi';

const blank = (): MissionSpec => ({
  name: '',
  brief: '',
  trigger: { kind: 'manual' },
  actions: [],
});
const labels: Record<WorkspaceAction['kind'], string> = {
  'campaign-summary': 'Read campaign summary',
  'campaign-start': 'Start campaign',
  'campaign-stop': 'Stop campaign (drain)',
  'campaign-rename': 'Rename campaign',
  'webhook-dispatch': 'Dispatch configured webhooks',
};
const MissionEditor = ({
  mission,
  onSaved,
  onDirtyChange,
  canManage,
  restoreSpec,
  publishedSpec,
  versions,
  runs,
}: {
  versions?: ReactNode;
  runs?: ReactNode;
  restoreSpec?: MissionSpec;
  publishedSpec?: MissionSpec;
  mission: Mission | null;
  onSaved: (id: string) => void;
  onDirtyChange: (v: boolean) => void;
  canManage: boolean;
}) => {
  const [spec, setSpec] = useState<MissionSpec>(restoreSpec ?? mission?.draft ?? blank());
  const [saved, setSaved] = useState(mission?.draft ?? blank());
  const [revision, setRevision] = useState(mission?.revision ?? 1);
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState('');
  const [review, setReview] = useState<'publish' | 'archive' | 'run' | null>(null);
  const id = useRef(mission?.id ?? crypto.randomUUID());
  const runId = useRef(crypto.randomUUID());
  const busy = useRef(false);
  const [create, { isLoading: creating }] = useCreateWorkspaceMissionMutation();
  const [save, { isLoading: saving }] = useSaveWorkspaceMissionMutation();
  const [publish, { isLoading: publishing }] = usePublishWorkspaceMissionMutation();
  const [pause, { isLoading: pausing }] = usePauseWorkspaceMissionMutation();
  const [archive, { isLoading: archiving }] = useArchiveWorkspaceMissionMutation();
  const [run, { isLoading: running }] = useRunWorkspaceMissionMutation();
  const loading = creating || saving || publishing || pausing || archiving || running;
  const stale = !!mission && mission.revision !== revision;
  const dirty = JSON.stringify(spec) !== JSON.stringify(saved);
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  const attempt = async (operation: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true;
    setError('');
    try {
      await operation();
    } catch (e) {
      setError(
        runtimeBudgetReasonMessage(e) ||
          getApiErrorMessage(
            e,
            'Unable to apply this change. Reload if another editor changed the mission.',
          ),
      );
    } finally {
      busy.current = false;
    }
  };
  const apply = () =>
    attempt(async () => {
      const result = mission
        ? await save({ id: id.current, revision, spec }).unwrap()
        : await create({ id: id.current, spec }).unwrap();
      setSaved(result.draft);
      setSpec(result.draft);
      setRevision(result.revision);
      onDirtyChange(false);
      onSaved(result.id);
    });
  const action = (index: number, patch: Partial<WorkspaceAction>) =>
    setSpec({
      ...spec,
      actions: spec.actions.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    });
  const campaigns = new Set(spec.actions.map((item) => item.campaignId).filter(Boolean)).size;
  const changing = spec.actions.some((item) => item.kind !== 'campaign-summary');
  const state = !mission?.active_version_id
    ? 'Draft'
    : mission.paused
      ? 'Paused'
      : publishedSpec && JSON.stringify(saved) !== JSON.stringify(publishedSpec)
        ? 'Draft changes'
        : 'Published';
  return (
    <Stack gap={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'start' }}
        gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography component="h1" variant="h3" sx={{ overflowWrap: 'anywhere' }}>
              {spec.name || 'New mission'}
            </Typography>
            <Chip label={dirty ? 'Unsaved changes' : state} size="small" variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {dirty
              ? 'Draft changes have not been saved.'
              : mission
                ? `Revision ${revision} · Saved on server`
                : 'Draft · Not saved yet'}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <MissionConfigurationChecks spec={spec} />
          <Button
            variant={mission ? 'outlined' : 'contained'}
            disabled={
              !canManage ||
              loading ||
              !dirty ||
              !spec.name.trim() ||
              !validMissionLimits(spec.limits) ||
              !spec.actions.length ||
              spec.actions.some((a) => !a.campaignId)
            }
            onClick={() => void apply()}>
            Save draft
          </Button>
          {mission && (
            <Button
              variant="contained"
              disabled={!canManage || loading || stale || dirty}
              onClick={() => setReview('publish')}>
              Review and publish
            </Button>
          )}
        </Stack>
      </Stack>
      {stale && (
        <Alert severity="warning">
          This mission changed on the server. Refresh to load its current revision before editing or
          changing its publication state.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Mission sections"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', minHeight: 48 },
        }}>
        <Tab value="overview" label="Overview" icon={<IconEye size={16} />} iconPosition="start" />
        <Tab value="trigger" label="Trigger" icon={<IconClock size={16} />} iconPosition="start" />
        <Tab value="access" label="Access" icon={<IconShield size={16} />} iconPosition="start" />
        <Tab
          value="runs"
          label="Runs"
          icon={<IconHistory size={16} />}
          iconPosition="start"
          disabled={!mission}
        />
        <Tab
          value="versions"
          label="Versions"
          icon={<IconStack size={16} />}
          iconPosition="start"
          disabled={!mission}
        />
      </Tabs>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 290px' },
          gap: 3,
          alignItems: 'start',
        }}>
        <Box sx={{ minWidth: 0 }}>
          <Paper
            component="section"
            aria-label="Mission overview"
            variant="outlined"
            sx={{ p: 3, display: tab === 'overview' ? 'block' : 'none' }}>
            <Stack gap={3}>
              <Typography variant="subtitle1" fontWeight={600}>
                The brief
              </Typography>
              <TextField
                label="Mission name"
                value={spec.name}
                disabled={!canManage || loading}
                slotProps={{ htmlInput: { maxLength: 120 } }}
                onChange={(e) => setSpec({ ...spec, name: e.target.value })}
              />

              <MissionBudgetFields
                value={spec.limits}
                disabled={!canManage || loading}
                onChange={(limits) => setSpec({ ...spec, limits })}
              />
              <MissionBriefEditor
                value={spec.brief}
                disabled={!canManage || loading}
                onChange={(brief) => setSpec({ ...spec, brief })}
              />
            </Stack>
          </Paper>
          <Paper
            component="section"
            aria-label="Mission trigger"
            variant="outlined"
            sx={{ p: 3, display: tab === 'trigger' ? 'block' : 'none' }}>
            <Stack gap={3}>
              <Typography variant="subtitle1" fontWeight={600}>
                When this mission runs
              </Typography>
              <TextField
                select
                label="Trigger"
                value={spec.trigger.kind}
                disabled={!canManage || loading}
                onChange={(e) =>
                  setSpec({
                    ...spec,
                    trigger:
                      e.target.value === 'schedule'
                        ? {
                            kind: 'schedule',
                            cron: '0 9 * * 1-5',
                            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                          }
                        : { kind: e.target.value as 'manual' | 'call-ended' },
                  })
                }>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="schedule">Schedule</MenuItem>
                <MenuItem value="call-ended">Call ended</MenuItem>
              </TextField>
              {spec.trigger.kind === 'schedule' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField
                    label="Cron (minute hour day month weekday)"
                    value={spec.trigger.cron ?? ''}
                    disabled={!canManage || loading}
                    onChange={(e) =>
                      setSpec({ ...spec, trigger: { ...spec.trigger, cron: e.target.value } })
                    }
                    fullWidth
                  />
                  <TextField
                    label="IANA timezone"
                    value={spec.trigger.timezone ?? ''}
                    disabled={!canManage || loading}
                    onChange={(e) =>
                      setSpec({ ...spec, trigger: { ...spec.trigger, timezone: e.target.value } })
                    }
                    fullWidth
                  />
                </Stack>
              )}
              {spec.trigger.kind === 'call-ended' && (
                <Stack gap={2}>
                  <Alert severity="info">
                    Runs for persisted ended calls in the selected campaigns after publication or
                    resume. Each event executes only actions targeting that event’s campaign. Leave
                    Call ID empty to use the triggering call.
                  </Alert>
                  <MissionEventFilters
                    spec={spec}
                    disabled={!canManage || loading}
                    onChange={(filters) =>
                      setSpec({ ...spec, trigger: { ...spec.trigger, filters } })
                    }
                  />
                </Stack>
              )}
            </Stack>
          </Paper>
          <Paper
            component="section"
            aria-label="Mission access and actions"
            variant="outlined"
            sx={{ p: 3, display: tab === 'access' ? 'block' : 'none' }}>
            <Stack gap={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Allowed actions &amp; scope
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only these explicit actions run after publication. Current company permissions,
                campaign state and call admission are checked again at execution.
              </Typography>
              {!canManage && (
                <Alert severity="info">
                  Campaign management permission is required to edit or publish.
                </Alert>
              )}
              {spec.actions.map((a, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                  <Stack gap={2}>
                    <TextField
                      select
                      label={`Action ${i + 1}`}
                      value={a.kind}
                      disabled={!canManage || loading}
                      onChange={(e) =>
                        action(i, {
                          kind: e.target.value as WorkspaceAction['kind'],
                          title: e.target.value === 'campaign-rename' ? a.title : undefined,
                          callId: e.target.value === 'webhook-dispatch' ? a.callId : undefined,
                        })
                      }>
                      {Object.entries(labels).map(([v, label]) => (
                        <MenuItem key={v} value={v}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <CampaignPicker
                      value={
                        a.campaignId
                          ? ({ id: a.campaignId, title: `Campaign ${a.campaignId}` } as Parameters<
                              typeof CampaignPicker
                            >[0]['value'])
                          : null
                      }
                      disabled={!canManage || loading}
                      onChange={(c) => action(i, { campaignId: c?.id ?? '' })}
                    />
                    {a.kind === 'campaign-rename' && (
                      <TextField
                        label="New campaign title"
                        value={a.title || ''}
                        disabled={!canManage || loading}
                        inputProps={{ maxLength: 120 }}
                        onChange={(e) => action(i, { title: e.target.value })}
                        required
                        helperText="Renames only an inactive campaign after permission and target checks."
                      />
                    )}
                    {a.kind === 'webhook-dispatch' && (
                      <TextField
                        label="Call ID"
                        helperText={
                          spec.trigger.kind === 'call-ended'
                            ? 'Optional: empty uses the triggering call.'
                            : 'Required for manual and scheduled dispatch.'
                        }
                        value={a.callId ?? ''}
                        disabled={!canManage || loading}
                        onChange={(e) => action(i, { callId: e.target.value || undefined })}
                      />
                    )}
                    <Button
                      color="error"
                      disabled={!canManage || loading}
                      onClick={() =>
                        setSpec({ ...spec, actions: spec.actions.filter((_, n) => n !== i) })
                      }>
                      Remove action
                    </Button>
                  </Stack>
                </Paper>
              ))}
              <Button
                disabled={!canManage || loading || spec.actions.length >= 8}
                onClick={() =>
                  setSpec({
                    ...spec,
                    actions: [...spec.actions, { kind: 'campaign-summary', campaignId: '' }],
                  })
                }>
                Add action
              </Button>
            </Stack>
          </Paper>
          <Box sx={{ display: tab === 'runs' ? 'block' : 'none' }}>{runs}</Box>
          <Box sx={{ display: tab === 'versions' ? 'block' : 'none' }}>{versions}</Box>
        </Box>
        <Stack component="aside" aria-label="Mission summary" gap={2}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack gap={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">At a glance</Typography>
                <IconClock size={17} />
              </Stack>
              {[
                [
                  'Trigger',
                  spec.trigger.kind === 'schedule'
                    ? spec.trigger.cron || 'Not configured'
                    : spec.trigger.kind === 'call-ended'
                      ? 'Call ended'
                      : 'Manual',
                ],
                [
                  'Timezone',
                  spec.trigger.kind === 'schedule'
                    ? spec.trigger.timezone || 'Not configured'
                    : 'Not applicable',
                ],
                ['Resources', `${campaigns} ${campaigns === 1 ? 'campaign' : 'campaigns'}`],
                [
                  'Access',
                  !spec.actions.length
                    ? 'Not configured'
                    : changing
                      ? 'Includes changes'
                      : 'Read only',
                ],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
              {mission?.next_due_at && (
                <Typography variant="caption" color="text.secondary">
                  Next scheduled occurrence: {new Date(mission.next_due_at).toLocaleString()}
                </Typography>
              )}
              {mission && (
                <>
                  <Divider />
                  <Button
                    disabled={!canManage || loading || mission.paused || !mission.active_version_id}
                    onClick={() => setReview('run')}>
                    Run published version
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack gap={2}>
              <Typography variant="subtitle2">Version control</Typography>
              <Typography variant="caption" color="text.secondary">
                Draft edits stay separate from the published version. Publishing requires a review
                of the exact actions.
              </Typography>
              {mission && (
                <>
                  <Button
                    disabled={!canManage || loading || stale || !mission.active_version_id}
                    onClick={() =>
                      void attempt(async () => {
                        await pause({ id: mission.id, revision, paused: !mission.paused }).unwrap();
                        onSaved(mission.id);
                      })
                    }>
                    {mission.paused ? 'Resume' : 'Pause'}
                  </Button>
                  <Button
                    color="error"
                    disabled={!canManage || loading}
                    onClick={() => setReview('archive')}>
                    Archive mission
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Box>
      <Dialog
        open={!!review}
        onClose={() => {
          if (!loading) setReview(null);
        }}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          {review === 'publish'
            ? 'Publish this version?'
            : review === 'archive'
              ? 'Archive this mission?'
              : 'Run the published version?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {review === 'publish'
              ? 'Publication authorizes these actions to run with your current permissions. Your brief is descriptive; it does not execute code. Schedules coalesce missed occurrences. Pausing prevents pending steps; it does not undo effects or end active calls.'
              : review === 'archive'
                ? 'The mission stops receiving triggers. Saved versions and run evidence remain.'
                : 'This runs the published actions, including any campaign changes or webhook deliveries. The draft is not executed.'}
          </Typography>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', overflow: 'auto' }}>
            {JSON.stringify(review === 'publish' ? spec : publishedSpec, null, 2)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setReview(null)}>
            Cancel
          </Button>
          <Button
            disabled={loading}
            variant="contained"
            onClick={() =>
              void attempt(async () => {
                if (review === 'publish') await publish({ id: id.current, revision }).unwrap();
                else if (review === 'archive') await archive({ id: id.current, revision }).unwrap();
                else {
                  await run({ id: id.current, requestId: runId.current }).unwrap();
                  runId.current = crypto.randomUUID();
                }
                setReview(null);
                onSaved(review === 'archive' ? '' : id.current);
              })
            }>
            Confirm {review}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export const MissionsWorkspace = ({
  initialDraft,
  initialMissionId,
  onDirtyChange,
  requestNavigation,
  onClose,
}: {
  onClose?: () => void;
  initialDraft?: MissionSpec;
  initialMissionId?: string;
  onDirtyChange: (v: boolean) => void;
  requestNavigation: (action: () => void) => void;
}) => {
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const [planner, setPlanner] = useState<{ seed?: MissionSpec; key: string } | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [restoreSpec, setRestoreSpec] = useState<MissionSpec | undefined>(initialDraft);
  const [selected, setSelected] = useState<string | null>(initialMissionId ?? null);
  const [creating, setCreating] = useState(!!initialDraft);
  const [after, setAfter] = useState<string>();
  const [previous, setPrevious] = useState<Mission[]>([]);
  const [editorKey, setEditorKey] = useState(0);
  const list = useWorkspaceMissionsQuery(after, {
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
  });
  const detail = useWorkspaceMissionQuery(selected ?? '', {
    skip: !selected,
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
  });
  const me = useUserMeControllerUserMeQuery();
  const canManage = me.data?.role !== 'viewer' && !!me.data?.privileges.includes('campaign.manage');
  const rows = [...previous, ...(list.currentData?.slice(0, 50) ?? [])];
  const navigate = (id: string | null, newMission = false, seed?: MissionSpec) =>
    requestNavigation(() => {
      onDirtyChange(false);
      setPlanner(null);
      setRestoreSpec(seed);
      setSelected(id);
      setCreating(newMission);
      setEditorKey((n) => n + 1);
    });
  const startNew = (seed?: MissionSpec) =>
    variant === 'v5'
      ? requestNavigation(() => setPlanner({ seed, key: crypto.randomUUID() }))
      : navigate(null, true, seed);
  useEffect(() => {
    if (variant !== 'v5' || workspace.module !== 'Missions' || !canManage) return;
    const shortcut = (event: KeyboardEvent) => {
      if (!isAssistantShortcut(event)) return;
      event.preventDefault();
      requestNavigation(() =>
        setPlanner((previous) => (previous ? null : { key: crypto.randomUUID() })),
      );
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [variant, workspace.module, canManage, requestNavigation]);
  const refresh = (
    <Button
      onClick={() =>
        requestNavigation(() => {
          void list.refetch();
          if (selected)
            void detail.refetch().then(() => {
              setRestoreSpec(undefined);
              setEditorKey((n) => n + 1);
              onDirtyChange(false);
            });
        })
      }>
      Refresh
    </Button>
  );
  const editing = creating || !!selected;
  const stateLabel = (mission: Mission) =>
    !mission.active_version_id ? 'Draft' : mission.paused ? 'Paused' : 'Published';
  const scopeLabel = (mission: Mission) => {
    const count = new Set(mission.draft.actions.map((action) => action.campaignId).filter(Boolean))
      .size;
    return `${count} ${count === 1 ? 'campaign' : 'campaigns'}`;
  };
  const filtered = rows.filter(
    (mission) =>
      mission.draft.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
      (status === 'all' || stateLabel(mission).toLocaleLowerCase() === status),
  );
  const playbooks = [
    {
      name: 'Daily campaign summary',
      brief: 'Review the current campaign summary each morning.',
      trigger: {
        kind: 'schedule',
        cron: '0 9 * * *',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
    {
      name: 'Weekly campaign summary',
      brief: 'Review the saved campaign state at the start of the week.',
      trigger: {
        kind: 'schedule',
        cron: '0 9 * * 1',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
    {
      name: 'After-call summary',
      brief: 'Read the campaign state after a call has ended.',
      trigger: { kind: 'call-ended' },
    },
  ] as const;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          md: planner ? 'minmax(0,1fr) 440px' : 'minmax(0,1fr)',
        },
        gap: 3,
        alignItems: 'start',
      }}>
      <Stack gap={3} sx={{ minWidth: 0 }}>
        {editing ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button startIcon={<IconArrowLeft size={15} />} onClick={() => navigate(null)}>
              All missions
            </Button>
            <Stack direction="row">
              {' '}
              {refresh}
              {onClose && (
                <Button onClick={() => requestNavigation(onClose)}>Back to workspace</Button>
              )}
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="start" gap={2}>
              <Box>
                <Typography component="h1" variant="h3">
                  Missions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  A clear brief. The right scope. Work that’s ready to repeat.
                </Typography>
              </Box>
              <Stack direction="row" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<IconPlus size={16} />}
                  disabled={!canManage}
                  onClick={() => startNew()}>
                  New mission
                </Button>
                {refresh}
                {onClose && (
                  <Button onClick={() => requestNavigation(onClose)}>Back to workspace</Button>
                )}
              </Stack>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
              }}>
              {[
                ['Loaded missions', rows.length],
                ['Published missions', rows.filter((item) => !!item.active_version_id).length],
                [
                  'Paused missions',
                  rows.filter((item) => !!item.active_version_id && item.paused).length,
                ],
              ].map(([label, value]) => (
                <Paper key={label} variant="outlined" sx={{ p: 2.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h3" sx={{ mt: 2 }}>
                    {value}
                  </Typography>
                </Paper>
              ))}
            </Box>
            {list.isError && <Alert severity="error">Unable to load saved missions.</Alert>}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={2}
                justifyContent="space-between"
                sx={{ p: 2 }}>
                <TextField
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search missions"
                  inputProps={{ 'aria-label': 'Search loaded missions' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch size={16} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { sm: 290 } }}
                />
                <TextField
                  select
                  label="Status"
                  size="small"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  sx={{ minWidth: 170 }}>
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                </TextField>
              </Stack>
              <TableContainer>
                <Table aria-label="Missions" sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      {['Mission', 'Trigger', 'Status', 'Scope', ''].map((label, index) => (
                        <TableCell key={index}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((mission) => (
                      <TableRow key={mission.id} hover>
                        <TableCell>
                          <Button
                            aria-label={`${mission.draft.name} · ${stateLabel(mission)}`}
                            onClick={() => navigate(mission.id)}
                            sx={{ p: 0, color: 'text.primary', fontWeight: 600 }}>
                            {mission.draft.name}
                          </Button>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {mission.draft.actions.some(
                              (action) => action.kind !== 'campaign-summary',
                            )
                              ? 'Includes changes'
                              : 'Read access'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {mission.draft.trigger.kind === 'schedule'
                            ? mission.draft.trigger.cron
                            : mission.draft.trigger.kind === 'call-ended'
                              ? 'Call ended'
                              : 'Manual'}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {mission.draft.trigger.timezone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={stateLabel(mission)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{scopeLabel(mission)}</TableCell>
                        <TableCell>
                          <Button
                            aria-label={`Open ${mission.draft.name}`}
                            onClick={() => navigate(mission.id)}>
                            <IconArrowRight size={17} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!filtered.length && (
                <Stack alignItems="center" gap={2} sx={{ px: 3, py: 8 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '50%',
                      color: 'primary.main',
                      bgcolor: 'primary.light',
                    }}>
                    <IconClock size={26} />
                  </Box>
                  <Typography variant="h6">
                    {rows.length ? 'No matching missions' : 'Your first mission starts with a goal'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ maxWidth: 430 }}>
                    Describe what to review, choose when it should happen, and publish the exact
                    campaigns and actions it can use.
                  </Typography>
                  <Button variant="outlined" disabled={!canManage} onClick={() => startNew()}>
                    Create a mission
                  </Button>
                </Stack>
              )}
              {(list.currentData?.length ?? 0) > 50 && (
                <Button
                  disabled={list.isFetching}
                  onClick={() => {
                    setPrevious(rows);
                    setAfter(rows.at(-1)!.id);
                  }}>
                  Load more missions
                </Button>
              )}
            </Paper>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Start with a playbook
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose a campaign and review the explicit actions before publishing.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 2,
                  mt: 2,
                }}>
                {playbooks.map((playbook) => (
                  <Paper key={playbook.name} variant="outlined" sx={{ p: 2.5 }}>
                    <Stack gap={2}>
                      <IconClock size={22} />
                      <Typography variant="body1" fontWeight={600}>
                        {playbook.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {playbook.brief}
                      </Typography>
                      <Button
                        disabled={!canManage}
                        endIcon={<IconArrowRight size={16} />}
                        onClick={() =>
                          startNew({
                            name: playbook.name,
                            brief: playbook.brief,
                            trigger: { ...playbook.trigger },
                            actions: [{ kind: 'campaign-summary', campaignId: '' }],
                          })
                        }
                        sx={{ alignSelf: 'start' }}>
                        Use playbook
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </Box>
          </>
        )}
        {detail.isError && <Alert severity="error">Unable to load this mission.</Alert>}
        {(creating || (selected && detail.currentData)) && (
          <MissionEditor
            key={`${selected ?? 'new'}:${editorKey}`}
            mission={creating ? null : detail.currentData!.mission}
            canManage={canManage}
            restoreSpec={restoreSpec}
            publishedSpec={
              detail.currentData?.versions.find(
                (v) => v.id === detail.currentData?.mission.active_version_id,
              )?.spec
            }
            runs={
              selected ? (
                <OperationJournal
                  key={selected}
                  missionId={selected}
                  versions={detail.currentData?.versions}
                />
              ) : null
            }
            versions={
              <Stack gap={3}>
                <MissionVersionHistory
                  versions={detail.currentData?.versions ?? []}
                  activeVersionId={detail.currentData?.mission.active_version_id}
                  draft={detail.currentData?.mission.draft}
                  canRestore={canManage}
                  onRestore={(version) =>
                    requestNavigation(() => {
                      onDirtyChange(false);
                      setCreating(false);
                      setRestoreSpec(version.spec);
                      setEditorKey((n) => n + 1);
                    })
                  }
                />
                {selected && (
                  <MissionSavedChecks
                    missionId={selected}
                    versions={detail.currentData?.versions ?? []}
                    canManage={canManage}
                  />
                )}
              </Stack>
            }
            onDirtyChange={onDirtyChange}
            onSaved={async (id) => {
              onDirtyChange(false);
              setRestoreSpec(undefined);
              setCreating(false);
              if (id === selected) await detail.refetch();
              setSelected(id || null);
              setEditorKey((n) => n + 1);
              void list.refetch();
            }}
          />
        )}
      </Stack>
      {planner && (
        <MissionPlanner
          key={planner.key}
          seed={planner.seed}
          canManage={canManage}
          onDirtyChange={onDirtyChange}
          onClose={() =>
            requestNavigation(() => {
              setPlanner(null);
              onDirtyChange(false);
            })
          }
          onApprove={(seed) => {
            setPlanner(null);
            setRestoreSpec(seed);
            setSelected(null);
            setCreating(true);
            setEditorKey((value) => value + 1);
          }}
        />
      )}
    </Box>
  );
};
